// components/SelfPaced/LessonPreviewModal.js
// Full-screen modal that lets admins preview lesson content (video, PDF, PPT, DOCX, text)
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import ReactPlayer from 'react-player';
import { X, AlertCircle } from 'lucide-react';
import { AppButton } from '../ui';
import ContentViewer from './ContentViewer';
import ContentWatermark from '../Common/ContentWatermark';
import { LESSON_TYPE_CONFIG } from './LessonItem';
import { getLessonContent, streamLessonVideo } from '../../utils/selfPacedApi';
import useVideoProtection from '../../hooks/useVideoProtection';
import { usePlan } from '../../contexts/PlanContext';
import { getCurrentUser } from '../../utils/auth';
import { isDesktopApp } from '../../utils/desktopApp';
import DesktopAppRequired from '../Common/DesktopAppRequired';
import '../../styles/VideoProtection.scss';
import './_self-paced-pages.scss';

const LessonPreviewModal = ({ show, onHide, lesson }) => {
  const { hasPlanFeature } = usePlan();
  const currentUser = getCurrentUser();

  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);
  const [courseVideoSecurity, setCourseVideoSecurity] = useState(false);
  const previewVideoRef = useRef(null);

  // Desktop app required only when plan+institute enabled AND course has videoSecurity on
  const requireDesktopApp = hasPlanFeature('videoSecurityEnabled') && courseVideoSecurity && !isDesktopApp() && currentUser?.usertype === 'student';

  useEffect(() => {
    if (!show || !lesson) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setContent(null);
      try {
        const res = await getLessonContent(lesson._id);
        const data = res.data?.data || res.data;
        if (!cancelled) {
          setContent(data);
          if (data?.videoSecurity !== undefined) setCourseVideoSecurity(!!data.videoSecurity);
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || 'Failed to load lesson content.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();

    return () => { cancelled = true; };
  }, [show, lesson]);

  // Derive video URL — same logic as StudentLearningPage
  const videoUrl = useMemo(() => {
    if (!lesson || lesson.type !== 'video') return null;
    if (content?.contentUrl) return content.contentUrl;
    if (content?.videoUrl) return content.videoUrl;
    if (content?.url) return content.url;
    return null;
  }, [lesson, content]);

  useVideoProtection({
    containerRef: previewVideoRef,
    onPause: () => {},
    onResume: () => {},
    active: show && lesson?.type === 'video' && !!videoUrl,
  });

  // Auto-refresh expired video URL
  const handleVideoError = useCallback(async () => {
    if (!lesson || lesson.type !== 'video') return;
    try {
      const res = await getLessonContent(lesson._id);
      setContent(res.data?.data || res.data);
    } catch {}
  }, [lesson]);

  const cfg = LESSON_TYPE_CONFIG[lesson?.type] || LESSON_TYPE_CONFIG.text;
  const Icon = cfg.icon;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="sp-preview-modal"
      backdrop="static"
    >
      <Modal.Header className="sp-preview-modal__header">
        <div className="sp-preview-modal__title-row">
          <span className="sp-preview-modal__type-badge" style={{ color: cfg.color, background: cfg.bg }}>
            <Icon size={14} /> {cfg.label}
          </span>
          <h5 className="sp-preview-modal__title">{lesson?.title || 'Lesson Preview'}</h5>
        </div>
        <button className="sp-preview-modal__close" onClick={onHide}>
          <X size={18} />
        </button>
      </Modal.Header>

      <Modal.Body className="sp-preview-modal__body">
        {loading && (
          <div className="sp-preview-modal__loading">
            <Spinner animation="border" variant="secondary" />
            <p>Loading preview…</p>
          </div>
        )}

        {error && (
          <div className="sp-preview-modal__error">
            <AlertCircle size={24} />
            <p>{error}</p>
            <AppButton size="sm" variant="outline" onClick={onHide}>Close</AppButton>
          </div>
        )}

        {!loading && !error && lesson?.type === 'video' && (
          requireDesktopApp ? (
            <DesktopAppRequired />
          ) : (
          <div className="sp-preview-modal__video vp-protected" ref={previewVideoRef}>
            <ContentWatermark videoMode />
            <div className="vp-shield" />
            <ReactPlayer
              url={videoUrl}
              controls
              width="100%"
              height="100%"
              onError={handleVideoError}
              config={{ file: { attributes: { controlsList: 'nodownload nofullscreen', disablePictureInPicture: true } } }}
            />
          </div>
          )
        )}

        {!loading && !error && lesson?.type === 'text' && (
          <div className="sp-preview-modal__content">
            <ContentViewer
              type="text"
              content={content?.content || content?.lesson?.content || ''}
              html={content?.html || content?.lesson?.html || ''}
              title={lesson.title}
            />
          </div>
        )}

        {!loading && !error && ['pdf', 'ppt', 'docx'].includes(lesson?.type) && (
          <div className="sp-preview-modal__content">
            <ContentViewer
              type={lesson.type}
              fileType={content?.lesson?.fileType || content?.fileType || ''}
              url={content?.contentUrl || content?.fileUrl || content?.url || ''}
              title={lesson.title}
              fileName={content?.lesson?.fileName || content?.fileName || ''}
            />
          </div>
        )}

        {!loading && !error && lesson?.type === 'quiz' && (
          <div className="sp-preview-modal__placeholder">
            <p>Quiz preview is available in the quiz editor.</p>
            <AppButton size="sm" variant="outline" onClick={onHide}>Close</AppButton>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default LessonPreviewModal;
